import React from 'react';
import { Printer } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Card } from 'shadcn/components/ui/Card';
import { cn } from 'shadcn/lib/utils';

import { isValidTagsPerPallet } from './printLabelsUtils';
import { PrintPalletLotRow } from './PrintPalletLotRow';

const NOOP = () => {};

/**
 * @param {Object} props
 * @param {string} [props.className]
 * @param {boolean} [props.isThirdPartyAsn]
 * @param {boolean} [props.isPrinting]
 * @param {{ id: string, lots: Array<object> }} props.pallet
 * @param {number} props.palletIndex
 * @param {string | null} [props.partnerDisplayName]
 * @param {number | ''} props.tagsPerPallet
 * @param {(lot: { id: string, caseCount: number }) => number | ''} [props.getCaseLabelCount]
 * @param {(lotId: string, qty: number | '') => void} [props.onCaseLabelCountChange]
 * @param {(lotId: string, qty: number) => void} [props.onPrintCaseTags]
 * @param {(palletId: string) => void} [props.onPrintPalletTags]
 */
function PrintPalletCaseTags({
    className,
    getCaseLabelCount = (lot) => lot.caseCount,
    isPrinting = false,
    isThirdPartyAsn = false,
    onCaseLabelCountChange = NOOP,
    onPrintCaseTags = NOOP,
    onPrintPalletTags = NOOP,
    pallet,
    palletIndex,
    partnerDisplayName = null,
    tagsPerPallet,
}) {
    const totalCases = pallet.lots.reduce((sum, lot) => sum + (Number(lot.caseCount) || 0), 0);
    const numLots = pallet.lots.length;
    const casesLabel = totalCases === 1 ? 'case' : 'cases';
    const lotsLabel = numLots === 1 ? 'lot' : 'lots';
    const canPrintPalletTags = isValidTagsPerPallet(tagsPerPallet);

    return (
        <Card className={cn('gap-0 border py-0 shadow-none', className)}>
            <div className="flex items-center justify-between gap-4 border-b px-3 py-1.5 text-sm">
                <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                    <span className="font-semibold leading-none">Pallet {pallet.id}</span>
                    <span className="text-xs text-muted-foreground">
                        {totalCases} {casesLabel} · {numLots} {lotsLabel}
                    </span>
                </div>
                <Button
                    className="h-8 shrink-0"
                    disabled={isPrinting || !canPrintPalletTags}
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => onPrintPalletTags(pallet.id)}
                >
                    <Printer aria-hidden className="size-4" />
                    Print Pallet Tags
                    <span className="text-xs text-muted-foreground">x{tagsPerPallet}</span>
                </Button>
            </div>
            <div className="px-3 py-1">
                {pallet.lots.map((lot, lotIndex) => (
                    <PrintPalletLotRow
                        key={lot.id}
                        caseLabelCount={getCaseLabelCount(lot)}
                        fieldIdPrefix={`print-pallet-${palletIndex}-lot-${lotIndex}`}
                        isThirdPartyAsn={isThirdPartyAsn}
                        isPrinting={isPrinting}
                        lot={lot}
                        lotIndex={lotIndex}
                        partnerDisplayName={partnerDisplayName}
                        onCaseLabelCountChange={onCaseLabelCountChange}
                        onPrintCaseTags={onPrintCaseTags}
                    />
                ))}
            </div>
        </Card>
    );
}

export { NOOP, PrintPalletCaseTags };
